import * as React from "react";
import CGReactApplication from "./cgreact-application";
import CGReactPage from "./cgreact-page";
import CGReactComponent from "./cgreact-component";
import {LayoutConfig, LayoutProp, LayoutState} from "../types/cgreact-type-layout";
import {copyObject, generateRandomColor, mergeObject, resetObject} from "./cgreact-util";
import CGReactModal from "./cgreact-modal";
import {WindowResizeSlot} from "./cgreact-slot";

export default class CGReactLayout<STATE extends LayoutState = LayoutState, PROP extends LayoutProp = LayoutProp> extends React.Component<PROP, STATE>
{
    /** CGReact组件类型*/
    public static CGReactType = "CGReactLayout";
    /** 通过React的ContextProvider机制获得父布局的div*/
    public static LayoutContext = React.createContext<CGReactLayout>(undefined);
    /** 使用异步集合队列处理机制减少加载处理次数*/
    private static AdjustLayout: CGReactLayout;
    /** 全局APP上下文*/
    public context: CGReactApplication;
    public parent: CGReactPage;
    public modal: CGReactModal;
    public component: CGReactComponent;
    /** @ts-ignore 去除State的只读属性*/
    public state: LayoutState;
    /**由CGReactComponent渲染的记录对应的组件*/
    /** 部分包含sideMenu的CGReact控件渲染的时候必须overflow设置为visible*/
    public OverflowVisible = false;
    /** 记录上次卷动的位置*/
    private ScrollLeft: number = 0;
    private ScrollTop: number = 0;
    /** 父布局的ref引用*/
    public ParentLayout: CGReactLayout;
    public ChildLayout: Set<CGReactLayout> = new Set<CGReactLayout>();
    /** 本布局的标准配置信息和祖先form表单的信息*/
    public LayoutConfig: LayoutConfig;
    public LayoutFormState: { theme: "default" | "pane", fluid: boolean; };
    /** 父布局和此布局的div元素引用*/
    public CurrentLayoutDivRef = React.createRef<HTMLDivElement>();
    public CurrentLayoutNameRef = React.createRef<HTMLSpanElement>();
    /** window大小重置插槽*/
    private WindowResize: WindowResizeSlot = {
        onWindowResize(data: UIEvent): void
        {
            console.log("ahhah eval");
            // 重设布局
            this.componentRenderOK();
            // 调用Component回调事件
            this.CGReactContent && this.CGReactContent.onCGReactLayoutResize();
        }
    };

    constructor(prop)
    {
        super(prop);
        /**  React的Context技术实现实现子组件的this.context为AppContext*/
        CGReactLayout.contextType = CGReactApplication.ApplicationContext;
        // 获取CG元素内容
        this.component = prop.CG;
        // 获取prop配置的state
        this.state = prop.layout ? prop.layout : {};
        // 构造布局配置参数
        this.state = mergeObject({
            layoutName: prop.layoutName,
            direction: prop.direction,
            layoutBorder: prop.layoutBorder,
            layoutDebug: prop.layoutDebug,
            layoutHeight: prop.layoutHeight,
            layoutWidth: prop.layoutWidth,
            gravity: prop.gravity,
            align: prop.align
        }, this.state);
    }

    componentDidMount(): void
    {
        // 生效布局样式
        this.affectStyle();
        // 初始化component的layout
        // this.component.layout = this;
        // 只有根布局才需要绑定信号
        !this.ParentLayout && this.context.WindowResizeSignal.bind(this.WindowResize, this);
        // 非CGReact控件自动调用autoAdjustLayout，CGReact控件由CGReactComponent调用autoAdjustLayout
        !this.component && this.CurrentLayoutDivRef.current && this.autoAdjustLayout();
    }

    componentWillUnmount(): void
    {
        // 解除layout的绑定
        this.ParentLayout && this.ParentLayout.ChildLayout.delete(this);
        // 接触插槽绑定
        this.context.WindowResizeSignal.unbind(this.WindowResize);
    }

    /** 根据state数据计算并生成config渲染配置*/
    private generateLayoutConfig(parent: CGReactLayout): LayoutConfig
    {
        // 计算direction
        let direction: any = this.constructor.name == "CGReactForm" ? "vertical" : "horizontal";
        direction = this.state.direction ? this.state.direction : direction;
        // 计算name属性
        let name = parent ? parent.LayoutConfig.layoutName + (Array.from(parent.ChildLayout).indexOf(this) + 1) : "1";
        // 计算debug属性
        let debug = parent && parent.state.layoutDebug ? parent.state.layoutDebug : "debug-off"; //默认用parent的配置，同时处理parent的undefined的情况
        debug = this.state.layoutDebug ? this.state.layoutDebug : debug; //如果本layout配置了，则优先级更高
        this.state.layoutDebug = debug; //保存到state
        // 计算with属性
        let width: any = this.state.layoutWidth ? this.state.layoutWidth : "fit-content"; //处理undefined的情况
        width = typeof width == "number" ? width + "px" : width; //处理number的情况
        // 计算height属性
        let height: any = this.state.layoutHeight ? this.state.layoutHeight : "fit-content";
        height = typeof height == "number" ? height + "px" : height;
        // 返回布局配置
        return {
            direction: direction, //方向默认横向
            layoutName: this.state.layoutName == undefined ? name : this.state.layoutName, //根布局的默认值是根布局
            layoutBorder: this.state.layoutBorder ? this.state.layoutBorder : "none",//border属性默认没有,
            layoutDebug: debug,//调试属性
            layoutWidthXS: typeof width == "string" ? width : width.phoneXS ? width.phoneXS : "fit-content",//超小屏幕宽度
            layoutWidthSM: typeof width == "string" ? width : width.padSM ? width.padSM : "fit-content",//小屏幕宽度
            layoutWidthMD: typeof width == "string" ? width : width.laptopMD ? width.laptopMD : "fit-content",//中屏幕宽度
            layoutWidthLG: typeof width == "string" ? width : width.desktopLG ? width.desktopLG : "fit-content",//大屏幕宽度
            layoutHeight: height,
            gravity: this.state.gravity ? this.state.gravity : "left-top",//子元素对其方式默认left-top
            align: this.state.align ? this.state.align : undefined,//默认没有个性化对其,
        };
    }

    render(): React.ReactElement<any>
    {
        return <React.Fragment>
            {/*获取parent上下文*/}
            <CGReactPage.ParentPageContext.Consumer>{parent => (this.parent = parent) && null}</CGReactPage.ParentPageContext.Consumer>
            {/*获取modal上下文*/}
            <CGReactModal.ModalContext.Consumer>{modal => (this.modal = modal) && null}</CGReactModal.ModalContext.Consumer>
            {/*渲染布局并获得父布局*/}
            <CGReactLayout.LayoutContext.Consumer>{layout.bind(this)}</CGReactLayout.LayoutContext.Consumer>
        </React.Fragment>;

        function layout(parent: CGReactLayout): JSX
        {
            // 记录父Layout的引用
            if (parent && !this.modal)
            {
                // 保存父
                this.ParentLayout = parent;
                this.ParentLayout.ChildLayout.add(this);
                this.LayoutFormState = parent.LayoutFormState;
            }
            // 计算布局参数
            this.LayoutConfig = this.generateLayoutConfig(parent);
            /** 表单如果是流体的，看布局配置，如果未配置width属性，则自动stretch-parent*/
            this.LayoutFormState && this.LayoutFormState.fluid && !this.state.layoutWidth && (this.LayoutConfig.layoutWidthXS = "stretch-parent");
            this.LayoutFormState && this.LayoutFormState.fluid && !this.state.layoutWidth && (this.LayoutConfig.layoutWidthSM = "stretch-parent");
            this.LayoutFormState && this.LayoutFormState.fluid && !this.state.layoutWidth && (this.LayoutConfig.layoutWidthMD = "stretch-parent");
            this.LayoutFormState && this.LayoutFormState.fluid && !this.state.layoutWidth && (this.LayoutConfig.layoutWidthLG = "stretch-parent");
            /** 渲染布局的内容*/
            let content = this.props.children instanceof Array ? this.props.children : [this.props.children];
            return <CGReactLayout.LayoutContext.Provider value={this}>
                {this.renderView(<React.Fragment>
                    {/*当处于debug模式的时候，渲染布局标签*/}
                    {this.LayoutConfig.layoutDebug != "debug-off" && <span ref={this.CurrentLayoutNameRef} style={{
                        position: "absolute",
                        wordBreak: "keep-all",
                        overflow: "hidden",
                        zIndex: 999
                    }}>{this.LayoutConfig.layoutName}</span>}
                    {/*渲染布局子元素*/}
                    {content.map((cur: any) => this.LayoutConfig.layoutDebug != "only-layout" || cur.CGReactModuleType ? cur : undefined)}
                </React.Fragment>, onLayoutScroll)}
            </CGReactLayout.LayoutContext.Provider>;

            function onLayoutScroll(e)
            {
                this.ScrollLeft = e.target.ScrollLeft;
                this.ScrollTop = e.target.scrollTop;
            }
        }
    }

    /** 生效样式*/
    protected affectStyle(): JSS
    {
        return undefined;
    }

    /** 渲染布局*/
    protected renderView(children: React.ReactElement, scroll: Function): JSX
    {
        let userStyle = copyObject(this.props.style);
        let layoutStyle = resetObject({
            display: 'inline-flex',
            boxSizing: "border-box"
        }, userStyle);
        return <div itemID={this.LayoutConfig.layoutName} className={this.props.className} ref={this.CurrentLayoutDivRef} style={layoutStyle} onScroll={scroll.bind(this)}>
            {children}
        </div>;
    }

    /** 自动调整布局，自适应布局
     * 1.对于直接使用CGReactLayout的情况，此函数有componentDidMount触发
     * 2.对于CGReactComponent的情况，此函数有对应组件在完成渲染后调用触发
     */
    public autoAdjustLayout(): void
    {
        // 从根局部上重新渲染，先找到根
        let rootLayout: CGReactLayout = this;
        while (rootLayout.ParentLayout)
        {
            rootLayout = rootLayout.ParentLayout;
        }
        // 所有布局都必须准备好
        let layoutArray = [rootLayout];
        while (layoutArray.length > 0)
        {
            let target = layoutArray.shift();
            if (!target.CurrentLayoutDivRef.current || (target.component && target.component["MountOK"] == false))
            {
                // 有布局没有准备好，一会它会重新触发这个逻辑，这里先返回
                return;
            }
            layoutArray.push(...Array.from(target.ChildLayout));
        }
        // 执行到这里找到了根布局，且所有子布局都准备好了，要保护一下200毫秒内只刷新一个根布局
        if (CGReactLayout.AdjustLayout == undefined)
        {
            CGReactLayout.AdjustLayout = rootLayout;
            window.setTimeout(() => CGReactLayout.AdjustLayout = undefined, 200);
            this.collectLayout(rootLayout);
        } else if (rootLayout != CGReactLayout.AdjustLayout)
        {
            console.warn(CGReactLayout.AdjustLayout.LayoutConfig.layoutName + "正在渲染，" + rootLayout.LayoutConfig.layoutName + "等一会");
        }
    }

    /** 自动确定需要进行布局调整的布局范围集合*/
    public collectLayout(rootLayout: CGReactLayout): void
    {
        /* compnentRenderOK已经实现了异步的调用保护机制，因此执行到这里组件已经全部加装完毕*/
        console.info(this.LayoutConfig.layoutName + " 使得 " + rootLayout.LayoutConfig.layoutName + " reset 了整个布局");
        // 进行布局的调整
        let layoutArray: Array<CGReactLayout> = [rootLayout];
        while (layoutArray.length > 0)
        {
            let target = layoutArray.shift();
            target.adjustLayout();
            layoutArray.push(...Array.from(target.ChildLayout));
        }
        // 还原滚动位置
        while (layoutArray.length > 0)
        {
            let cur = layoutArray.shift();
            cur.CurrentLayoutDivRef.current.scrollTop = cur.ScrollTop;
            cur.CurrentLayoutDivRef.current.scrollLeft = cur.ScrollLeft;
            let childs = Array.from(cur.ChildLayout);
            layoutArray.push(...childs);
        }
    }

    /** 调整当前布局，此函数不仅限于当前的布局，作为公有可能被其他的Layout调用，当调整的是当前的Layout*/
    public adjustLayout(): void
    {
        /* 获取相关配置参数*/
        let config = this.LayoutConfig;
        // 获得元素引用和parent参数值
        let currentDiv = this.CurrentLayoutDivRef.current;
        let parentDiv = currentDiv.parentElement;
        let parentStyle = window.getComputedStyle(parentDiv);
        let parentHeight = parentDiv.clientHeight - Number(parentStyle.paddingTop) - Number(parentStyle.paddingBottom) - Number(parentStyle.borderTopWidth) - Number(parentStyle.borderBottomWidth);
        let parentWidth = parentDiv.clientWidth - Number(parentStyle.paddingLeft) - Number(parentStyle.paddingRight) - Number(parentStyle.borderLeftWidth) - Number(parentStyle.borderRightWidth);
        // 特殊处理根布局的计算值获取
        if (!this.ParentLayout)
        {
            currentDiv.style.position = "fixed";
            let bodyStyle = window.getComputedStyle(document.body);
            let parentCalWidth = window.innerWidth - Number(bodyStyle.marginLeft) - Number(bodyStyle.marginRight) - Number(bodyStyle.paddingLeft) - Number(bodyStyle.paddingRight) - 2;
            let parentCalHeight = window.innerHeight - Number(bodyStyle.marginTop) - Number(bodyStyle.marginBottom) - Number(bodyStyle.paddingTop) - Number(bodyStyle.paddingBottom) - 9;
            currentDiv.style.position = "relative";
            parentHeight = parentCalHeight;
            parentWidth = parentCalWidth;
        }
        //获得当前dom长宽属性
        let scrollBarLength = 17;
        let currentStyle = window.getComputedStyle(currentDiv);
        let marginLeft = Number(currentStyle.marginLeft);
        let marginRight = Number(currentStyle.marginRight);
        let marginTop = Number(currentStyle.marginTop);
        let marginBottom = Number(currentStyle.marginBottom);
        let paddingLeft = Number(currentStyle.paddingLeft);
        let paddingRight = Number(currentStyle.paddingRight);
        let paddingTop = Number(currentStyle.paddingTop);
        let paddingBottom = Number(currentStyle.paddingBottom);
        // 获得当前宽度的使用键
        let widthKey = parentWidth <= 768 ? config.layoutWidthXS : "fit-content"; //超小屏幕
        widthKey = parentWidth > 768 && parentWidth <= 992 ? config.layoutWidthSM : widthKey;//小屏幕
        widthKey = parentWidth > 992 && parentWidth <= 1200 ? config.layoutWidthMD : widthKey;//中屏幕
        widthKey = parentWidth > 1200 ? config.layoutWidthLG : widthKey;//大屏幕
        // 初始化比例percent转化映射表
        let convertMap = {
            "grid-1": "8.33333333%",
            "grid-2": "16.66666667%",
            "grid-3": "25%",
            "grid-4": "33.33333333%",
            "grid-5": "41.66666667%",
            "grid-6": "50%",
            "grid-7": "58.33333333%",
            "grid-8": "66.66666667%",
            "grid-9": "75%",
            "grid-10": "83.33333333%",
            "grid-11": "91.66666667%",
            "grid-12": "100%"
        };
        /** 计算生效基础布局参数*/
        // 基础参数生效
        currentDiv.style.display = "inline-flex";// 不管用户怎么配使用flex布局
        currentDiv.style.boxSizing = "border-box"; //border作为宽度的一部分
        currentDiv.style.flexWrap = "nowrap";//不允许换行
        currentDiv.style.flexDirection = config.direction == "vertical" ? "column" : "row"; //方向配置
        currentDiv.style.overflow = "visible"; //先visible后面还会自动计算
        // 计算并生效背景颜色
        let backgroundColor = config.layoutDebug != "debug-off" ? generateRandomColor() : undefined;//背景颜色debug下随机生成
        (this.props.style == undefined || (this.props.style.background == undefined && this.props.style.backgroundColor == undefined)) && (currentDiv.style.backgroundColor = backgroundColor);//优先用户配置
        // 计算并生效border，优先使用配置
        let border = config.layoutBorder == "default" ? "1px solid #fdfdfd" : undefined; //处理default的边框配置
        border = config.layoutDebug != "debug-off" ? "1px solid black" : border; //调试模式优先级更高
        (this.props.style == undefined || this.props.style.border == undefined) && (currentDiv.style.border = border);
        /*计算布局对其和偏移*/
        // 计算主轴对齐
        let justifyContent: "flex-start" | "center" | "flex-end" = "flex-start";//默认start
        justifyContent = config.direction == "horizontal" && (config.gravity == "left-top" || config.gravity == "left-bottom" || config.gravity == "left-center") ? "flex-start" : justifyContent;
        justifyContent = config.direction == "horizontal" && (config.gravity == "middle-top" || config.gravity == "middle-bottom" || config.gravity == "middle-center") ? "center" : justifyContent;
        justifyContent = config.direction == "horizontal" && (config.gravity == "right-top" || config.gravity == "right-bottom" || config.gravity == "right-center") ? "flex-end" : justifyContent;
        justifyContent = config.direction == "vertical" && (config.gravity == "left-top" || config.gravity == "right-top" || config.gravity == "middle-top") ? "flex-start" : justifyContent;
        justifyContent = config.direction == "vertical" && (config.gravity == "left-bottom" || config.gravity == "right-bottom" || config.gravity == "middle-bottom") ? "flex-end" : justifyContent;
        justifyContent = config.direction == "vertical" && (config.gravity == "left-center" || config.gravity == "right-center" || config.gravity == "middle-center") ? "center" : justifyContent;
        currentDiv.style.justifyContent = justifyContent; //无视用户配置生效
        // 计算辅轴对齐
        let alignItems: "flex-start" | "center" | "flex-end" = "flex-start";//默认start
        alignItems = config.direction == "vertical" && (config.gravity == "left-top" || config.gravity == "left-bottom" || config.gravity == "left-center") ? "flex-start" : justifyContent;
        alignItems = config.direction == "vertical" && (config.gravity == "middle-top" || config.gravity == "middle-bottom" || config.gravity == "middle-center") ? "center" : justifyContent;
        alignItems = config.direction == "vertical" && (config.gravity == "right-top" || config.gravity == "right-bottom" || config.gravity == "right-center") ? "flex-end" : justifyContent;
        alignItems = config.direction == "horizontal" && (config.gravity == "left-top" || config.gravity == "right-top" || config.gravity == "middle-top") ? "flex-start" : justifyContent;
        alignItems = config.direction == "horizontal" && (config.gravity == "left-bottom" || config.gravity == "right-bottom" || config.gravity == "middle-bottom") ? "flex-end" : justifyContent;
        alignItems = config.direction == "horizontal" && (config.gravity == "left-center" || config.gravity == "right-center" || config.gravity == "middle-center") ? "center" : justifyContent;
        currentDiv.style.alignItems = alignItems; //无视用户配置生效
        /*计算并生效布局宽度*/
        if (widthKey.indexOf("px") > 0)
        {
            // 处理直接配置一个固定值的情况
            currentDiv.style.width = widthKey;
            currentDiv.style.minWidth = widthKey;
        } else if (widthKey == "fit-content")
        {
            currentDiv.style.width = "fit-content";
            currentDiv.style.minWidth = config.layoutDebug == "only-layout" ? "50px" : "unset";
        } else if (widthKey == "stretch-content")
        {
            // 处理stretch-content的情况
            if (this.ParentLayout)
            {
                this.ParentLayout.LayoutConfig.direction == "vertical" ? currentDiv.style.width = "100%" : currentDiv.style.flexGrow = "12";
                currentDiv.style.minWidth = currentDiv.clientWidth + "px";//要先获得伸展后的clientWidth
            } else
            {
                currentDiv.style.minWidth = parentWidth - marginLeft - marginRight + "px";
            }
            currentDiv.style.width = "fit-content";
        } else
        {
            // 处理配置 grid-*和stretch-parent的情况
            widthKey = widthKey == "stretch-parent" ? "grid-12" : widthKey;
            if (this.ParentLayout)
            {
                if (this.ParentLayout.LayoutConfig.direction == "horizontal" && parentDiv == this.ParentLayout.CurrentLayoutDivRef.current)
                {
                    currentDiv.style.width = "unset";
                    currentDiv.style.flexGrow = widthKey.substr(widthKey.indexOf("-") + 1);
                } else
                {
                    currentDiv.style.width = convertMap[widthKey];
                }
            } else
            {
                currentDiv.style.width = parentWidth * Number(convertMap[widthKey]) + "px";
            }
            currentDiv.style.minWidth = "unset";
        }
        /* 计算并生效布局高度 */
        let heightKey = config.layoutHeight;
        if (heightKey.indexOf("px") > 0)
        {
            // 处理直接配置一个固定值的情况
            currentDiv.style.height = heightKey;
            currentDiv.style.minHeight = heightKey;
        } else if (heightKey == "fit-content")
        {
            currentDiv.style.height = "fit-content";
            currentDiv.style.minHeight = config.layoutDebug == "only-layout" ? "50px" : "unset";
        } else if (heightKey == "stretch-content")
        {
            // 处理stretch-content的情况
            if (this.ParentLayout)
            {
                this.ParentLayout.LayoutConfig.direction == "horizontal" ? currentDiv.style.height = "100%" : currentDiv.style.flexGrow = "12";
                currentDiv.style.minHeight = currentDiv.clientHeight + "px";//要先获得伸展后的clientHeight
            } else
            {
                currentDiv.style.minHeight = parentHeight - marginTop - marginBottom + "px";
            }
            currentDiv.style.width = "fit-content";
        } else
        {
            // 处理配置 grid-*和stretch-parent的情况
            heightKey = heightKey == "stretch-parent" ? "grid-12" : heightKey;
            if (this.ParentLayout)
            {
                if (this.ParentLayout.LayoutConfig.direction == "vertical" && parentDiv == this.ParentLayout.CurrentLayoutDivRef.current)
                {
                    currentDiv.style.height = "unset";
                    currentDiv.style.flexGrow = heightKey.substr(heightKey.indexOf("-") + 1);
                } else
                {
                    currentDiv.style.height = convertMap[heightKey];
                }
            } else
            {
                currentDiv.style.height = parentHeight * Number(convertMap[heightKey]) + "px";
            }
            currentDiv.style.minHeight = "unset";
        }
        /* 处理标签文字长度越界导致渲染计算不准确的问题*/
        let labelDiv = this.CurrentLayoutNameRef.current;
        labelDiv && (labelDiv.style.width = Math.min(currentDiv.clientWidth, labelDiv.offsetWidth) + "px");
        // 计算个性化对其
        if (config.align)
        {
            // 计算参数
            let middleLeft = (parentWidth / 2 - (currentDiv.clientWidth + marginLeft + marginRight) / 2) + "px";
            let rightLeft = (parentWidth - (currentDiv.clientWidth + marginLeft + marginRight)) + "px";
            let centerTop = (parentHeight / 2 - (currentDiv.clientHeight + marginTop + marginBottom) / 2) + "px";
            let bottomTop = (parentHeight - (currentDiv.clientHeight + marginTop + marginBottom)) + "px";
            // 计算水平偏移
            let left = config.align == "left-top" || config.align == "left-bottom" || config.align == "left-center" ? "0px" : undefined;
            left = config.align == "middle-top" || config.align == "middle-bottom" || config.align == "middle-center" ? middleLeft : left;
            left = config.align == "right-top" || config.align == "right-bottom" || config.align == "right-center" ? rightLeft : left;
            // 计算垂直偏移
            let top = config.align == "left-top" || config.align == "right-top" || config.align == "middle-top" ? "0px" : undefined;
            top = config.align == "left-center" || config.align == "right-center" || config.align == "middle-center" ? centerTop : top;
            top = config.align == "left-bottom" || config.align == "right-bottom" || config.align == "middle-bottom" ? bottomTop : top;
            // 生效偏移
            currentDiv.style.position = "absolute";
            currentDiv.style.left = left;
            currentDiv.style.top = top;
        } else
        {
            currentDiv.style.position = "relative";
        }
        /* 计算并生效overflow*/
        let overFlowX = (widthKey == "fit-content" || widthKey == "stretch-content" || widthKey.indexOf("px") > 0) ? "visible" : "auto";
        let overFlowY = (heightKey == "fit-content" || heightKey == "stretch-content" || heightKey.indexOf("px") > 0) ? "visible" : "auto";
        // 部分控件指定必须可视
        overFlowX = this.OverflowVisible ? "visible" : overFlowX;
        overFlowY = this.OverflowVisible ? "visible" : overFlowY;
        //用户配置优先级更高
        if (this.props.style)
        {
            overFlowX = this.props.style.overflow ? this.props.style.overflow : overFlowX;
            overFlowX = this.props.style.overflowX ? this.props.style.overflowX : overFlowX;
            overFlowY = this.props.style.overflow ? this.props.style.overflow : overFlowY;
            overFlowY = this.props.style.overflowY ? this.props.style.overflowY : overFlowY;
        }
        /* 生效溢出配置,这可能干扰到获取正确的clientWidth和clientHeight*/
        currentDiv.style.overflowX = "visible"; //水平方向永远visible
        currentDiv.style.overflowY = overFlowY;
    }
}
